import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISSED_KEY = 'timebank_dismissed_banners';

// Note: localStorage is used here ONLY for non-sensitive UI state
// (an array of banner_ids the user has dismissed). No PII or auth tokens
// touch this storage. XSS concerns around localStorage do not apply.
const readDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeDismissed = (ids) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch (err) {
    // localStorage may be unavailable (private mode, quota exceeded) — UI still works.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Banner: could not persist dismissed list', err);
    }
  }
};

const TYPE_STYLES = {
  info:     { bg: 'bg-[#c7dfe9]',  icon: Info,         iconColor: 'text-blue-700' },
  success:  { bg: 'bg-green-200',  icon: CheckCircle,  iconColor: 'text-green-700' },
  warning:  { bg: 'bg-[#ffd703]',  icon: AlertTriangle, iconColor: 'text-black' },
  critical: { bg: 'bg-[#e056a0]',  icon: AlertCircle,  iconColor: 'text-white', textColor: 'text-white' },
};

export function Banner() {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/banner/active`);
        if (!res.data) return;
        const dismissed = readDismissed();
        if (!dismissed.includes(res.data.banner_id)) {
          setBanner(res.data);
        }
      } catch (err) {
        // Banner is optional — failures here should NOT break the page.
        // Intentional silent catch; surface in dev only.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Banner: failed to fetch active banner', err);
        }
      }
    };
    fetchBanner();
  }, []);

  const handleDismiss = () => {
    if (!banner) return;
    const dismissed = readDismissed();
    if (!dismissed.includes(banner.banner_id)) {
      dismissed.push(banner.banner_id);
      writeDismissed(dismissed);
    }
    setBanner(null);
  };

  if (!banner) return null;

  const style = TYPE_STYLES[banner.type] || TYPE_STYLES.info;
  const Icon = style.icon;
  const textColor = style.textColor || 'text-black';

  return (
    <div
      className={`${style.bg} ${textColor} border-b-[3px] border-black`}
      data-testid="site-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${style.iconColor}`} />
        <p className="flex-1 text-sm sm:text-base font-sans font-semibold" data-testid="banner-message">
          {banner.message}
        </p>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center border-2 border-black bg-white/80 hover:bg-white transition-colors"
          aria-label="Dismiss banner"
          data-testid="banner-dismiss"
        >
          <X className="w-4 h-4 text-black" />
        </button>
      </div>
    </div>
  );
}
